"use client";
import React, { ReactNode } from "react";

import { cn } from "@/utils/cn";

export interface FormWrapperProps {
  label?: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
  labelRight?: ReactNode;
  errorClassName?: string;
  className?: string;
  insideRightComponent?: ReactNode;
  insideLeftComponent?: ReactNode;
  insideRightComponentPosition?: "top" | "bottom" | "center";
  insideLeftComponentPosition?: "top" | "bottom" | "center";
}

/**
 * A universal wrapper for form fields with consistent error display.
 * Provides standard label and error styling across all form components.
 */
export const FormWrapper: React.FC<FormWrapperProps> = ({
  label,
  required = false,
  error,
  children,
  labelRight,
  errorClassName,
  insideRightComponent,
  insideLeftComponent,
  insideRightComponentPosition = "center",
  insideLeftComponentPosition = "center",
  className,
}) => {
  return (
    <div
      className={cn("form-wrapper", error && "form-wrapper-error", className)}
    >
      {label && (
        <div className="form-wrapper-label-container">
          <label
            suppressHydrationWarning
            className={cn(
              "form-wrapper-label",
              error && "form-wrapper-label-error",
            )}
          >
            {label}
            {required && (
              <span
                suppressHydrationWarning
                className="form-wrapper-required"
              >
                *
              </span>
            )}
          </label>
          {labelRight && (
            <div className="form-wrapper-label-right">{labelRight}</div>
          )}
        </div>
      )}

      <div
        className={cn(
          "form-wrapper-content",
          insideLeftComponent && "form-wrapper-content-left",
          insideRightComponent && "form-wrapper-content-right",
        )}
      >
        {insideLeftComponent && (
          <div
            className={cn(
              "absolute left-8",
              insideLeftComponentPosition === "top" && "top-8",
              insideLeftComponentPosition === "bottom" && "bottom-8",
              insideLeftComponentPosition === "center" &&
                "top-1/2 -translate-y-1/2",
            )}
          >
            {insideLeftComponent}
          </div>
        )}
        {children}
        {insideRightComponent && (
          <div
            className={cn(
              "absolute left-8",
              insideRightComponentPosition === "top" && "top-8",
              insideRightComponentPosition === "bottom" && "bottom-8",
              insideRightComponentPosition === "center" &&
                "top-1/2 -translate-y-1/2",
            )}
          >
            {insideRightComponent}
          </div>
        )}
      </div>

      {error && (
        <div className={cn("form-wrapper-error-message", errorClassName)}>
          {error}
        </div>
      )}
    </div>
  );
};

export default FormWrapper;
